import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { setFrameworkOverrides } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { useGetListOutreachConfigQuery } from "@/pages/Private/redux/list/list-outreach.api";
import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";

import { resolveGenerateBarPrefill } from "./generateBarPrefill";

interface UseGenerateBarPrefillArgs {
	/** The list the draft is written for; `0` is "no list" at both `GenerateBar` mount sites. */
	listId: number;
	prospectId: number;
	/** The bar's own language state setter — the prefill seeds it, it is not owned here. */
	setLanguage: (language: string) => void;
}

export interface GenerateBarPrefillHandle {
	/** Hand this to the language controls: a user pick also cancels any pending prefill. */
	selectLanguage: (language: string) => void;
	/** Call when the user picks frameworks, so a late-arriving config cannot overwrite them. */
	keepUserFrameworks: () => void;
}

/**
 * Opens a manual generation on the list's chat language and framework (PREC-3687 D8 / AC 18),
 * both still freely changeable.
 *
 * It fires the config query itself: the only other callers are `ListEditCopywriting` and
 * `ReachOutMenu`, so the lead's Chat tab — the headline surface of AC 18 — would otherwise read an
 * empty slice bucket forever. The value is still read through the selector and never from the
 * query hook's `data`, because a warm cache entry dispatches no `fulfilled` (apps/web CLAUDE.md §2).
 *
 * A lead is seeded **once**: `seededForRef` records the list+lead the prefill (or the user) has
 * already settled, so a rerender, a socket update or a second draft for the same lead never
 * re-seeds, while paging to another lead does.
 */
export const useGenerateBarPrefill = ({
	listId,
	prospectId,
	setLanguage,
}: UseGenerateBarPrefillArgs): GenerateBarPrefillHandle => {
	const dispatch = useAppDispatch();

	useGetListOutreachConfigQuery(listId, { skip: !listId });

	// `0` means "no list" — never `list/0/outreach-config`, and nothing to prefill from.
	const config = useAppSelector(listOutreachConfigSelector(listId || undefined));

	const seededForRef = useRef<string | null>(null);

	useEffect(() => {
		const draftKey = `${listId}:${prospectId}`;

		if (!listId || seededForRef.current === draftKey) {
			return;
		}

		const prefill = resolveGenerateBarPrefill(config);

		// The config has not landed in the slice yet — a later render applies it.
		if (!prefill) {
			return;
		}

		seededForRef.current = draftKey;

		setLanguage(prefill.language);

		if (prefill.frameworkOverrides) {
			dispatch(setFrameworkOverrides(prefill.frameworkOverrides));
		}
	}, [config, dispatch, listId, prospectId, setLanguage]);

	const markSettled = useCallback(() => {
		seededForRef.current = `${listId}:${prospectId}`;
	}, [listId, prospectId]);

	const selectLanguage = useCallback(
		(language: string) => {
			markSettled();
			setLanguage(language);
		},
		[markSettled, setLanguage]
	);

	return { selectLanguage, keepUserFrameworks: markSettled };
};
